if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('servings');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        // Only set active media if there's no current user selection (preserve user's thumbnail choice)
        const currentActiveMedia = this.querySelector(`media-gallery [aria-current="true"]`);
        if (!currentActiveMedia) {
          this.querySelector(`media-gallery`)?.setActiveMedia?.(
            `${this.dataset.section}-${variantFeaturedMediaId}`,
            true
          );
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

if (!customElements.get('product-image-gallery')) {
  customElements.define(
    'product-image-gallery',
    class ProductImageGallery extends HTMLElement {
      constructor() {
        super();
        this.thumbSwiper = null;
        this.mainSwiper = null;
        this.swiperEl = this.querySelector('.product-image-slider');
        this.thumbSwiperEl = this.querySelector('.product-image-thumbsSlider');
        this.variantChangeUnsubscriber = null;
      }

      connectedCallback() {
        this.initializeSwipers();
        this.subscribeToVariantChanges();
        this.initMobileZoom();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      subscribeToVariantChanges() {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const { html, variant } = event.data;
          if (variant && html) {
            this.reloadGallery(html);
          }
        });
      }

      reloadGallery(html) {
        const newGallery = html.querySelector('product-image-gallery');
        if (!newGallery) return;

        // Add smooth transition
        this.style.opacity = '0';
        this.style.transition = 'opacity 0.2s ease';

        setTimeout(() => {
          // Destroy existing swipers
          if (this.mainSwiper) {
            this.mainSwiper.destroy(true, true);
            this.mainSwiper = null;
          }
          if (this.thumbSwiper) {
            this.thumbSwiper.destroy(true, true);
            this.thumbSwiper = null;
          }

          // Replace the entire gallery content
          const newMainSlider = newGallery.querySelector('.product-image-slider');
          const newThumbSlider = newGallery.querySelector('.product-image-thumbsSlider');

          if (newMainSlider && newThumbSlider) {
            this.swiperEl.innerHTML = newMainSlider.innerHTML;
            this.thumbSwiperEl.innerHTML = newThumbSlider.innerHTML;

            // Update data attribute
            const newVariantId = newGallery.dataset.currentVariant;
            this.dataset.currentVariant = newVariantId;

            // Reinitialize swipers and fade back in
            this.initializeSwipers();
            // Re-initialize zoom for new slides
            this.initImageZoom();
            // Re-attach mobile zoom listeners for new slides
            if (this._mobileZoomAttachListeners) {
              this._mobileZoomAttachListeners();
            }
            requestAnimationFrame(() => {
              this.style.opacity = '1';
            });
          }
        }, 200);
      }

      initializeSwipers() {
        if (this.mainSwiper || this.thumbSwiper) return;

        this.thumbSwiper = new Swiper(this.thumbSwiperEl, {
          spaceBetween: 8,
          slidesPerView: 6.4,
          freeMode: true,
          breakpoints: {
            991: {
              spaceBetween: 10,
            },
          },
        });

        this.mainSwiper = new Swiper(this.swiperEl, {
          spaceBetween: 10,
          slidesPerView: 1,
          loop: false,
          mousewheel: true,
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          pagination: {
            el: '.swiper-pagination',
            type: 'fraction',
          },
          thumbs: {
            swiper: this.thumbSwiper,
          },
          breakpoints: {
            991: {
              slidesPerView: 1.095,
            },
          },
        });

        // Initialize image zoom after swiper is ready
        this.initImageZoom();
      }

      initImageZoom() {
        // Only run on desktop (viewport width >= 1024px)
        if (!window.matchMedia('(min-width: 1024px)').matches) return;

        const slides = this.swiperEl.querySelectorAll('.swiper-slide');

        slides.forEach((slide) => {
          const img = slide.querySelector('img');
          if (!img) return;

          // Skip if already initialized
          if (slide.dataset.zoomInitialized) return;
          slide.dataset.zoomInitialized = 'true';

          // Add smooth transition
          img.style.transition = 'transform 0.3s ease-out';

          let isHovering = false;

          slide.addEventListener('mouseenter', function () {
            isHovering = true;
          });

          slide.addEventListener('mousemove', function (e) {
            if (!isHovering) return;

            const rect = slide.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            const xPercent = x / rect.width;
            const yPercent = y / rect.height;

            const transformOriginX = xPercent * 100;
            const transformOriginY = yPercent * 100;

            img.style.transformOrigin = `${transformOriginX}% ${transformOriginY}%`;
            img.style.transform = 'scale(1.5)';
          });

          slide.addEventListener('mouseleave', function () {
            isHovering = false;
            img.style.transform = 'scale(1)';

            setTimeout(() => {
              if (!isHovering) {
                img.style.transformOrigin = 'center center';
              }
            }, 300);
          });
        });
      }

      initMobileZoom() {
        if (this.dataset.mobileZoom !== 'true') return;
        if (!window.matchMedia('(max-width: 991px)').matches) return;

        const viewer = this.querySelector('.mobile-zoom-viewer');
        if (!viewer) return;

        const closeBtn = viewer.querySelector('[data-close]');
        const draggable = viewer.querySelector('[data-viewer-image]');
        const viewerImg = draggable.querySelector('img');

        let isDragging = false;
        let startX = 0;
        let startY = 0;
        let initialX = 0;
        let initialY = 0;
        let imageViewerOpen = false;
        let currentScroll = 0;

        const disableBodyScroll = () => {
          currentScroll = window.scrollY;
          document.body.style.overflow = 'hidden';
          document.body.style.position = 'fixed';
          document.body.style.top = `-${currentScroll}px`;
          document.body.style.width = '100%';
        };

        const enableBodyScroll = () => {
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.top = '';
          document.body.style.width = '';
          window.scrollTo(0, currentScroll);
        };

        const setInitialPosition = () => {
          if (!imageViewerOpen) return;
          const topPos = (viewerImg.offsetHeight - window.innerHeight) / 2;
          const leftPos = (viewerImg.offsetWidth - window.innerWidth) / 2;
          draggable.style.top = `${-topPos}px`;
          draggable.style.left = `${-leftPos}px`;
        };

        const waitForTransition = (el) => {
          return new Promise((resolve) => {
            const handler = (e) => {
              if (e.target === el) {
                el.removeEventListener('transitionend', handler);
                resolve();
              }
            };
            el.addEventListener('transitionend', handler);
            setTimeout(resolve, 600);
          });
        };

        // Move viewer to body so it sits above all stacking contexts (header, announcement bar)
        document.body.appendChild(viewer);

        const openViewer = (imgSrc) => {
          viewerImg.src = imgSrc;
          viewerImg.setAttribute('loading', 'eager');
          draggable.classList.add('overflow-hidden');

          const doOpen = () => {
            imageViewerOpen = true;
            viewer.classList.remove('is-hidden');
            viewer.setAttribute('aria-hidden', 'false');

            setTimeout(() => {
              setInitialPosition();
              disableBodyScroll();
              viewer.classList.add('is-visible');

              waitForTransition(viewer).then(() => {
                draggable.classList.remove('overflow-hidden');
                closeBtn.classList.remove('is-transparent');
              });
            }, 50);
          };

          if (viewerImg.complete && viewerImg.naturalWidth > 0) {
            doOpen();
          } else {
            viewerImg.onload = doOpen;
          }
        };

        const closeViewer = () => {
          closeBtn.classList.add('is-transparent');
          imageViewerOpen = false;
          draggable.classList.add('overflow-hidden');
          enableBodyScroll();

          setTimeout(() => {
            viewer.classList.remove('is-visible');
            waitForTransition(viewer).then(() => {
              viewer.classList.add('is-hidden');
              viewer.setAttribute('aria-hidden', 'true');
              viewerImg.src = '';
            });
          }, 66);
        };

        // Touch drag
        draggable.addEventListener('touchstart', (e) => {
          isDragging = true;
          startX = e.changedTouches[0].clientX;
          startY = e.changedTouches[0].clientY;
          initialX = draggable.offsetLeft;
          initialY = draggable.offsetTop;
          draggable.classList.add('is-dragging');
        }, { passive: true });

        draggable.addEventListener('touchmove', (e) => {
          if (!isDragging) return;
          e.preventDefault();
          const touch = e.changedTouches[0];
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          const newLeft = initialX + dx;
          const newTop = initialY + dy;

          if (newLeft < 1 && newLeft + viewerImg.offsetWidth > window.innerWidth) {
            draggable.style.left = newLeft + 'px';
          }
          if (newTop < 1 && newTop + viewerImg.offsetHeight > window.innerHeight) {
            draggable.style.top = newTop + 'px';
          }
        }, { passive: false });

        draggable.addEventListener('touchend', () => {
          isDragging = false;
          draggable.classList.remove('is-dragging');
        });

        // Mouse drag (for DevTools simulation)
        draggable.addEventListener('mousedown', (e) => {
          isDragging = true;
          startX = e.clientX;
          startY = e.clientY;
          initialX = draggable.offsetLeft;
          initialY = draggable.offsetTop;
          draggable.classList.add('is-dragging');
        });

        document.addEventListener('mousemove', (e) => {
          if (!isDragging) return;
          const dx = e.clientX - startX;
          const dy = e.clientY - startY;
          const newLeft = initialX + dx;
          const newTop = initialY + dy;

          if (newLeft < 1 && newLeft + viewerImg.offsetWidth > window.innerWidth) {
            draggable.style.left = newLeft + 'px';
          }
          if (newTop < 1 && newTop + viewerImg.offsetHeight > window.innerHeight) {
            draggable.style.top = newTop + 'px';
          }
        });

        document.addEventListener('mouseup', () => {
          if (isDragging) {
            isDragging = false;
            draggable.classList.remove('is-dragging');
          }
        });

        // Close
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          closeViewer();
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && imageViewerOpen) closeViewer();
        });

        window.addEventListener('resize', () => {
          if (imageViewerOpen) requestAnimationFrame(setInitialPosition);
        });

        // Attach click to slide images
        const attachSlideListeners = () => {
          const slides = this.swiperEl.querySelectorAll('.swiper-slide img');
          slides.forEach((img) => {
            if (img.dataset.mobileZoomBound) return;
            img.dataset.mobileZoomBound = 'true';
            img.style.cursor = 'zoom-in';
            img.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              openViewer(img.src);
            });
          });
        };

        attachSlideListeners();

        if (this.mainSwiper) {
          this.mainSwiper.on('slideChange', () => attachSlideListeners());
        }

        this._mobileZoomAttachListeners = attachSlideListeners;
      }
    }
  );
}